import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createApiClient } from '../lib/apiClient';
import { setPassword as setPasswordApi } from '../lib/auth/authApi';
import { useAuth } from '../lib/auth/AuthProvider';
import './advisor.css';

/**
 * Where the emailed link lands.
 *
 * Two things this screen is careful about:
 *
 * 1. It signs you in on success. The server already issued a session — making
 *    someone set a password and then immediately type it again is asking them
 *    to prove they can remember something from four seconds ago.
 *
 * 2. It asks for the password twice. A typo in the only credential that reaches
 *    your firm's client list, on a link that works once, is a support call at
 *    best. The confirmation costs three seconds and removes that entirely.
 *
 * The guidance says LENGTH, not symbols, because that is what actually makes a
 * password hard to guess — and because a rule demanding a symbol is how people
 * end up with "P@ssw0rd", which is weaker than four ordinary words.
 */
export function SetPasswordScreen() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { adoptSession } = useAuth();

  const ticket = params.get('t') ?? '';
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const bare = createApiClient({ tokens: { get: () => null, set: () => {} } });

  const tooShort = password.length > 0 && password.length < 12;
  const mismatch = again.length > 0 && again !== password;
  const ready = ticket !== '' && password.length >= 12 && again === password && !busy;

  async function handleSubmit(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const tokens = await setPasswordApi(bare, ticket, password);
      adoptSession(tokens);
      navigate('/', { replace: true });
    } catch (e) {
      setError(e instanceof Error && e.message !== '' ? e.message : 'We could not set your password.');
    } finally {
      setBusy(false);
    }
  }

  if (ticket === '') {
    return (
      <div className="auth">
        <div className="auth__panel la-card la-rise">
          <p className="la-label">Legacy Architect</p>
          <h1 className="auth__title">This link is incomplete</h1>
          <p className="auth__body">
            Open the link from your email exactly as it was sent, or ask for a new one from the
            sign-in page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth">
      <div className="auth__panel la-card la-rise">
        <p className="la-label">Legacy Architect</p>
        <h1 className="auth__title">Choose your password</h1>
        <p className="auth__body">
          This is how you sign in from any device. Length matters more than symbols &mdash; a few
          ordinary words together is both stronger and easier to live with.
        </p>

        <label className="auth__label" htmlFor="password">New password</label>
        <input
          id="password"
          className="auth__input"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        <label className="auth__label" htmlFor="again">Type it again</label>
        <input
          id="again"
          className="auth__input"
          type="password"
          autoComplete="new-password"
          value={again}
          onChange={(e) => setAgain(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && ready) void handleSubmit(); }}
        />

        <div className="la-cta-wrap">
          <button type="button" className="la-btn-primary" disabled={!ready} onClick={() => void handleSubmit()}>
            {busy ? 'Saving' : 'Set password and sign in'}
          </button>
        </div>

        {tooShort && (
          <p className="auth__body auth__body--hint">
            A little longer &mdash; at least 12 characters.
          </p>
        )}
        {mismatch && (
          <p className="auth__body auth__body--hint">Those two don&rsquo;t match yet.</p>
        )}
        {error !== null && (
          <p className="auth__alert" role="alert">{error}</p>
        )}

        {/* Said here because this is the moment someone forms their idea of what
            the password is for, and the wrong idea is dangerous in both
            directions: complacency about the password, or an assumption that it
            alone will open a sealed message years from now. */}
        <p className="auth__body auth__body--hint">
          Your password signs you in. It does not by itself open a client&rsquo;s sealed
          messages &mdash; nobody at the firm can read those.
        </p>
      </div>
    </div>
  );
}
